import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Plus, BookOpen, Edit3, Trash2, Eye, EyeOff, X, Upload, Image as ImageIcon } from "lucide-react";

const CATEGORIES = ["Blog", "FAQ"];

export default function BlogStudio({ tier }) {
  const canEdit = tier === "ops" || tier === "founder";
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const uploadRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchAll = async () => {
    try {
      const { data } = await http.get("/blog/posts?all=1");
      setPosts(data.posts || []);
    } catch (e) {
      console.error("Failed to load blog posts:", e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  if (!canEdit) return null;

  const createNew = () => setEditing({ title: "", slug: "", category: "Blog", body: "", excerpt: "", cover_url: "", published: false });

  const onPickCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await http.post("/blog/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, cover_url: data.url }));
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const payload = {
      title: editing.title.trim(),
      slug: (editing.slug || editing.title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80),
      category: editing.category,
      body: editing.body.trim(),
      excerpt: (editing.excerpt || "").trim(),
      cover_url: editing.cover_url || "",
      published: !!editing.published,
    };
    if (!payload.title || !payload.body) return toast.error("Title and body are required");
    try {
      if (editing.id) {
        await http.patch(`/blog/posts/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await http.post("/blog/posts", payload);
        toast.success("Created");
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const togglePublish = async (p) => {
    try {
      await http.patch(`/blog/posts/${p.id}`, { published: !p.published });
      fetchAll();
    } catch (e) { toast.error("Toggle failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await http.delete(`/blog/posts/${p.id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <section data-testid="blog-studio" className="mt-6 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#0B1E3F]" />
          <h3 className="font-serif text-lg">Blog & FAQ studio</h3>
          <span className="text-[10px] uppercase tracking-widest text-slate-400">{posts.length} posts</span>
        </div>
        <button
          data-testid="blog-new-btn"
          onClick={createNew}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1E3F] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#162A4D]"
        >
          <Plus className="w-3.5 h-3.5" /> New post
        </button>
      </div>

      {loading && <div className="text-sm text-slate-400">Loading…</div>}
      {!loading && posts.length === 0 && <div className="text-sm text-slate-400">No posts yet.</div>}

      <div className="space-y-2">
        {posts.map((p) => (
          <div
            key={p.id}
            data-testid={`blog-row-${p.id}`}
            className="flex flex-wrap items-center gap-3 border border-slate-100 rounded-xl px-4 py-3 bg-slate-50/40"
          >
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800">{p.title}</span>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${p.published ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {p.published ? "Live" : "Draft"}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{p.category}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">/{p.slug}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid={`blog-toggle-${p.id}`}
                onClick={() => togglePublish(p)}
                title={p.published ? "Unpublish" : "Publish"}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 inline-flex items-center justify-center text-slate-600"
              >
                {p.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button
                data-testid={`blog-edit-${p.id}`}
                onClick={() => setEditing(p)}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 inline-flex items-center justify-center text-slate-600"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`blog-delete-${p.id}`}
                onClick={() => remove(p)}
                className="w-8 h-8 rounded-lg border border-rose-200 bg-white hover:bg-rose-50 inline-flex items-center justify-center text-rose-600"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          data-testid="blog-editor-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
        >
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="font-serif text-xl">{editing.id ? "Edit post" : "New post"}</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500">Title</span>
                <input
                  data-testid="blog-title-input"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-500">Slug (optional)</span>
                  <input
                    data-testid="blog-slug-input"
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="auto-from-title"
                    className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-500">Category</span>
                  <select
                    data-testid="blog-category-select"
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200 bg-white"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500">Excerpt</span>
                <input
                  data-testid="blog-excerpt-input"
                  value={editing.excerpt || ""}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  placeholder="Short teaser shown on blog index"
                  className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200"
                />
              </label>

              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Cover image</div>
                {editing.cover_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200">
                    <img
                      src={editing.cover_url.startsWith("http") ? editing.cover_url : `${process.env.REACT_APP_BACKEND_URL}${editing.cover_url}`}
                      alt=""
                      className="w-full aspect-[16/8] object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, cover_url: "" })}
                      data-testid="blog-cover-remove"
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => uploadRef.current?.click()}
                    data-testid="blog-cover-upload"
                    disabled={uploading}
                    className="w-full rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 py-6 text-sm text-slate-500 hover:border-[#0B1E3F] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {uploading ? <>Uploading…</> : <><Upload className="w-4 h-4" /> Upload cover (PNG · JPG · WEBP, &lt; 4 MB)</>}
                  </button>
                )}
                <input
                  ref={uploadRef}
                  data-testid="blog-cover-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => onPickCover(e.target.files?.[0])}
                />
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500">Body</span>
                <textarea
                  data-testid="blog-body-input"
                  rows={12}
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  placeholder="Plain text or markdown — paragraphs separated by blank lines render as-is."
                  className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200 font-sans"
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  data-testid="blog-published-toggle"
                  type="checkbox"
                  checked={!!editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                />
                Publish now
              </label>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-xl border border-slate-200 bg-white hover:bg-slate-100 px-4 py-2 text-sm font-semibold">Cancel</button>
              <button
                data-testid="blog-save-btn"
                onClick={save}
                className="rounded-xl bg-[#0B1E3F] hover:bg-[#162A4D] text-white px-4 py-2 text-sm font-semibold"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
